import { countGoalsByLifeArea, sortItems } from '@griply/utils';
import { useMemo } from 'react';
import { useDataGridSorting } from 'shared/hooks/use-data-grid-sorting';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import { LifeAreaColumnType } from '../types';
import { mapLifeAreaToRowData } from '../utils/map-life-area-to-row-data';
import { sortLifeAreaRows } from '../utils/sort-life-area-rows';

export const useLifeAreaOverviewRows = (
  lifeAreas: LifeArea[],
  goals: Goal[],
  lifeAreaSorting: ID[] | null | undefined,
) => {
  const {
    sortMode,
    type: sortColumn,
    onSortChange,
  } = useDataGridSorting<LifeAreaColumnType>();

  const links = useEntityLinks();

  const countsByLifeArea = useMemo(
    () => countGoalsByLifeArea(goals, links),
    [goals, links],
  );

  const rowsByState = useMemo(() => {
    const toRow = (lifeArea: LifeArea) =>
      mapLifeAreaToRowData(lifeArea, countsByLifeArea);

    return {
      active: sortItems(
        lifeAreas.filter(
          ({ frozenAt, archivedAt }) => !frozenAt && !archivedAt,
        ),
        lifeAreaSorting,
        'id',
      ).map(toRow),
      frozen: lifeAreas.filter(({ frozenAt }) => !!frozenAt).map(toRow),
      archived: sortItems(
        lifeAreas.filter(({ archivedAt }) => !!archivedAt),
        lifeAreaSorting,
        'id',
      ).map(toRow),
    };
  }, [lifeAreas, lifeAreaSorting, countsByLifeArea]);

  const activeRows = useMemo(
    () => sortLifeAreaRows(rowsByState.active, sortColumn, sortMode),
    [rowsByState.active, sortColumn, sortMode],
  );

  return {
    activeRows,
    frozenRows: rowsByState.frozen,
    archivedRows: rowsByState.archived,
    sortMode,
    sortColumn,
    onSortChange,
  };
};
